import {XH} from '@xh/hoist/core';
import {LayoutOptions} from 'cytoscape';

export function getStyleObj() {
    return [...getNodeStyles(), ...getEdgeStyles(), ...getEdgeDrawStyles()];
}

export function getDefaultLayout(): LayoutOptions {
    return {name: 'dagre'};
}

type ThemedColors = {
    textColor: string;
    backgroundColor: string;
    activeColor: string;
    selectedColor: string;
    edgeColor: string;
};

const defaultNodeShape = 'ellipse';
const defaultWidth = 75;
const defaultHeight = 75;
const defaultFontSize = '12px';
const defaultEdgeLineStyle = 'solid';

function getThemedColors(): ThemedColors {
    if (XH.darkTheme) {
        return {
            textColor: 'white',
            backgroundColor: '#263238',
            activeColor: 'red',
            selectedColor: 'red',
            edgeColor: 'grey'
        };
    } else {
        return {
            textColor: 'black',
            backgroundColor: '#bdbdbd',
            activeColor: 'blue',
            selectedColor: 'blue',
            edgeColor: 'grey'
        };
    }
}

function getNodeStyles(): any[] {
    const themedColors = getThemedColors();

    return [
        {
            selector: 'node',
            style: {
                'text-valign': 'center',
                'text-halign': 'center',
                shape: node => node.data().style?.shape ?? defaultNodeShape,
                width: node => node.data().style?.width ?? defaultWidth,
                height: node => node.data().style?.height ?? defaultHeight,
                display: node => (node.data().style?.display === 'none' ? 'none' : 'element'),
                content: node => node.data().label ?? '',
                color: node => node.data().style?.color ?? themedColors.textColor,
                'background-color': node =>
                    node.data().style?.backgroundColor ?? themedColors.backgroundColor,
                'font-size': node => node.data().style?.fontSize ?? defaultFontSize
            }
        },
        {
            selector: ':selected',
            style: {
                'border-width': '10px',
                'border-style': 'solid',
                'border-color': themedColors.activeColor,
                'background-color': themedColors.activeColor
            }
        },
        {
            selector: 'node.hover',
            style: {
                'border-width': '5px',
                'border-style': 'dotted',
                'border-color': themedColors.activeColor,
                'background-color': themedColors.activeColor
            }
        }
    ];
}

export function getEdgeStyles(): any[] {
    const themedColors = getThemedColors();

    return [
        {
            selector: 'edge',
            style: {
                display: edge => (edge.data().style?.display === 'none' ? 'none' : 'element'),
                'target-arrow-shape': 'triangle',
                'curve-style': 'straight',
                'line-style': edge => {
                    return edge.data().style?.lineStyle ?? defaultEdgeLineStyle;
                },
                'line-color': edge => {
                    return edge.data().style?.color ?? themedColors.edgeColor;
                },
                'target-arrow-color': edge => {
                    return edge.data().style?.color ?? themedColors.edgeColor;
                }
            }
        },
        {
            selector: 'edge.hover',
            style: {
                'line-color': themedColors.activeColor,
                'target-arrow-color': themedColors.activeColor
            }
        },
        {
            selector: 'edge:selected',
            style: {
                'line-color': themedColors.activeColor,
                'target-arrow-color': themedColors.activeColor
            }
        }
    ];
}

export function getEdgeDrawStyles(): any[] {
    const themedColors = getThemedColors();

    return [
        {
            selector: '.eh-handle',
            style: {
                'background-color': themedColors.activeColor,
                width: 12,
                height: 12,
                shape: 'ellipse',
                'overlay-opacity': 0,
                'border-width': 12,
                'border-opacity': 0
            }
        },
        {
            selector: '.eh-hover',
            style: {
                'background-color': themedColors.activeColor
            }
        },
        {
            select: '.eh-source',
            style: {
                'border-width': 10,
                'border-color': themedColors.activeColor
            }
        },
        {
            selector: '.eh-target',
            style: {
                'border-width': 10,
                'border-color': themedColors.activeColor
            }
        },
        {
            selector: '.eh-preview, .eh-ghost-edge',
            style: {
                'background-color': themedColors.activeColor,
                'line-color': themedColors.activeColor,
                'target-arrow-color': themedColors.activeColor,
                'source-arrow-color': themedColors.activeColor
            }
        },
        {
            selector: '.eh-ghost-edge.eh-preview-active',
            style: {
                opacity: 0
            }
        }
    ];
}
